import { useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import { Calendar } from 'react-calendar';
import { PrimaryButton } from '../../../../shared/ui/Inputs';
import { formatDate } from '../../../../utils/date';
import { Spinner } from '../../../../shared/ui/Spinners';

const today = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const tomorrow = () => {
  const date = today();
  date.setDate(date.getDate() + 1);
  return date;
};

/** @returns {import('./deliveryConfiguration.types').DeliveryValue} */
export const createDeliveryValue = () => ({
  deliveryType: 'entrega_agendada',
  deliveryDate: tomorrow(),
  deliveryTurn: { manha: false, tarde: false, noite: false },
});

/**
 * @param {{ selectedPatient?: any, orderType: string, onFinish?: (date: Date, turn: import('./deliveryConfiguration.types').DeliveryValue['deliveryTurn']) => unknown, fastDeliveryLabel: string, scheduleDeliveryLabel: string, _hasFastDelivery: boolean, savingOrder?: boolean, addressRender?: () => import('react').ReactNode, value?: import('./deliveryConfiguration.types').DeliveryValue, onChange?: (value: import('./deliveryConfiguration.types').DeliveryValue) => void, showFinishAction?: boolean }} props
 */
export const DeliveryConfiguration = ({
  selectedPatient,
  orderType,
  onFinish,
  fastDeliveryLabel,
  scheduleDeliveryLabel,
  _hasFastDelivery,
  savingOrder,
  addressRender,
  value = undefined,
  onChange = undefined,
  showFinishAction = true,
}) => {
  const [localValue, setLocalValue] = useState(createDeliveryValue);
  const currentValue = value ?? localValue;
  const { deliveryType, deliveryDate, deliveryTurn } = currentValue;
  const update = (patch) => {
    const next = { ...currentValue, ...patch };
    if (value === undefined) setLocalValue(next);
    onChange?.(next);
  };
  const setDeliveryDate = (date) => update({ deliveryDate: date });
  const setDeliveryTurn = (turn) => update({ deliveryTurn: turn });
  const calendarMinDate =
    deliveryType === 'entrega_agendada' ? tomorrow() : today();
  const hasFastDelivery = () => orderType !== 'Implantação' || _hasFastDelivery;
  const formatTurn = () => {
    let turnos = [];
    if (
      deliveryTurn['manha'] &&
      deliveryTurn['tarde'] &&
      deliveryTurn['noite']
    ) {
      return '';
    }
    if (deliveryTurn['manha']) {
      turnos.push('manhã');
    }
    if (deliveryTurn['tarde']) {
      turnos.push('tarde');
    }
    if (deliveryTurn['noite']) {
      turnos.push('noite');
    }
    if (turnos.length > 0) {
      return ' no período da ' + turnos.join(' ou ');
    }
    return '';
  };
  return (
    <div style={{ display: 'block', width: '100%' }}>
      <div>
        <h4 style={{ margin: '0 0 5px', padding: '0', fontWeight: '700' }}>
          Endereço
        </h4>
        {typeof addressRender === 'function' ? (
          addressRender()
        ) : (
          <ul
            style={{
              listStyle: 'none',
              marginTop: '0px',
              marginBottom: '10px',
              padding: '10px',
              background: 'rgb(247, 247, 247)',
              borderRadius: '11px',
            }}
          >
            <li style={{ margin: '0px', padding: '0px' }}>
              <label style={{ fontWeight: '300', fontSize: '14px' }}>
                {selectedPatient.address} - {selectedPatient.number}
              </label>
            </li>
            <li style={{ margin: '0px', padding: '0px' }}>
              <label style={{ fontWeight: '300', fontSize: '14px' }}>
                {selectedPatient.city} - {selectedPatient.uf}
              </label>
            </li>
            <li style={{ margin: '0px', padding: '0px' }}>
              <label style={{ fontWeight: '300', fontSize: '14px' }}>
                {selectedPatient.cep}
              </label>
            </li>
          </ul>
        )}
      </div>

      <div>
        <h4 style={{ margin: '0 0 5px', padding: '0', fontWeight: '700' }}>
          Agendamento para {orderType.toLowerCase()}
        </h4>
        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
              marginTop: '0px',
              marginBottom: '10px',
              padding: '10px',
              background: 'rgb(247, 247, 247)',
              borderRadius: '11px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginRight: '15px',
              }}
            >
              <input
                disabled={!hasFastDelivery()}
                checked={deliveryType === 'entrega_imediata'}
                onChange={() => {
                  const date = new Date();
                  if (date.getHours() + 1 > 20)
                    date.setDate(date.getDate() + 1);
                  update({
                    deliveryType: 'entrega_imediata',
                    deliveryDate: date,
                  });
                }}
                aria-label={fastDeliveryLabel}
                type="radio"
                value="entrega_imediata"
                name="entrega"
              />
              <span style={{ fontSize: '14px', color: 'black' }}>
                {fastDeliveryLabel}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginRight: '15px',
              }}
            >
              <input
                checked={deliveryType === 'entrega_agendada'}
                onChange={() =>
                  update({
                    deliveryType: 'entrega_agendada',
                    deliveryDate: tomorrow(),
                  })
                }
                aria-label={scheduleDeliveryLabel}
                type="radio"
                value="entrega_agendada"
                name="entrega"
              />
              <span style={{ fontSize: '14px', color: 'black' }}>
                {scheduleDeliveryLabel}
              </span>
            </div>
          </div>
          {deliveryType === 'entrega_agendada' ? (
            <div>
              <h4
                style={{
                  margin: '0 0 5px',
                  padding: '0',
                  fontWeight: '700',
                }}
              >
                Período
              </h4>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginTop: '0px',
                  marginBottom: '10px',
                  padding: '10px',
                  background: 'rgb(247, 247, 247)',
                  borderRadius: '11px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    marginRight: '15px',
                  }}
                >
                  <input
                    onChange={(e) =>
                      setDeliveryTurn({
                        ...deliveryTurn,
                        manha: e.target.checked,
                      })
                    }
                    type="checkbox"
                    checked={deliveryTurn.manha}
                    aria-label="Manhã"
                    value="manha"
                  />
                  <span style={{ fontSize: '14px', color: 'black' }}>
                    Manhã
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '15px',
                  }}
                >
                  <input
                    onChange={(e) =>
                      setDeliveryTurn({
                        ...deliveryTurn,
                        tarde: e.target.checked,
                      })
                    }
                    type="checkbox"
                    checked={deliveryTurn.tarde}
                    aria-label="Tarde"
                    value="tarde"
                  />
                  <span style={{ fontSize: '14px', color: 'black' }}>
                    Tarde
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '15px',
                  }}
                >
                  <input
                    onChange={(e) =>
                      setDeliveryTurn({
                        ...deliveryTurn,
                        noite: e.target.checked,
                      })
                    }
                    type="checkbox"
                    checked={deliveryTurn.noite}
                    aria-label="Noite"
                    value="noite"
                  />
                  <span style={{ fontSize: '14px', color: 'black' }}>
                    Noite
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
          <div
            style={
              deliveryType !== 'entrega_agendada'
                ? {
                    pointerEvents: 'none',
                    marginTop: '15px',
                    opacity: '0.25',
                  }
                : { marginTop: '15px' }
            }
          >
            <Calendar
              onChange={(e) => setDeliveryDate(e)}
              locale="pt"
              minDate={calendarMinDate}
              value={deliveryDate}
            />
          </div>
          <div>
            {deliveryDate !== null ? (
              <>
                {/* <h4 style={{color:"black"}}>Finalizar</h4> */}
                <p
                  style={{
                    marginTop: '10px',
                    marginBottom: '10px',
                    padding: '10px',
                    background: 'rgb(247, 247, 247)',
                    borderRadius: '11px',
                  }}
                >
                  {showFinishAction
                    ? 'Pedido agendado para'
                    : 'Agendamento previsto para'}{' '}
                  <span style={{ fontWeight: '700' }}>
                    {formatDate(deliveryDate)}
                  </span>{' '}
                  <span style={{ fontWeight: '700' }}>{formatTurn()}</span>
                </p>
                {showFinishAction && (
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    {savingOrder ? (
                      <Spinner
                        className="dark"
                        style={{ transform: 'scale(0.8)' }}
                      />
                    ) : (
                      <PrimaryButton
                        type="button"
                        onClick={() => onFinish?.(deliveryDate, deliveryTurn)}
                        disabled={savingOrder}
                        style={{
                          minWidth: '140px',
                          height: '42px',
                          fontSize: '15px',
                          fontWeight: '700',
                        }}
                      >
                        Finalizar
                      </PrimaryButton>
                    )}
                  </div>
                )}
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
